import { notifications } from '@mantine/notifications';
import { useApi } from 'api/api-context';
import useImmediateAction from 'api/use-immediate-action';
import PreDefinedDataForm, { PreDefinedDataFormData } from 'components/forms/pre-defined-data/PreDefinedDataForm';
import { PREDEFINED_DATA_REDIRECT_AFTER_SAVE } from 'env';
import panic from 'errors/panic';
import DashboardLayout from 'layouts/dashboard/DashboardLayout';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { USERS_PREDEFINED_DATA_PAGE_PATH } from 'routes/paths';
import { SUCCESS_NOTIFICATION_COLOR } from 'utils/constants';

/**
 * Page used to edit user's pre-defined data.
 *
 */
export default function EditUsersPredefinedDataPage() {
  const navigate = useNavigate();
  const { predefinedDataId } = useParams();
  const { getAction } = useApi();

  const { data, loading, error } = useImmediateAction(() => {
    const preDefinedDataGetAction = getAction('PredefinedDataGetUsers');
    return preDefinedDataGetAction({ parameters: { usersPredefinedDataId: predefinedDataId! } });
  });

  if (error) {
    panic(error);
    return <></>;
  }

  if (!predefinedDataId) {
    return <Navigate to={USERS_PREDEFINED_DATA_PAGE_PATH.original} />;
  }

  /**
   * Handles the form submission.
   */
  function onSubmit({ predefinedDataName, predefinedDataDescription, predefinedDataTypeId }: PreDefinedDataFormData) {
    const preDefinedDataUpdateAction = getAction('PredefinedDataUpdateUsers');

    return preDefinedDataUpdateAction({
      payload: {
        predefinedDataName,
        predefinedDataDescription: predefinedDataDescription ?? undefined,
      },
      parameters: {
        usersPredefinedDataId: predefinedDataId!,
      },
    })
      .then(() => {
        notifications.show({
          title: 'Predvolený údaj bol upravený',
          message: `Predvolený údaj ${predefinedDataName} bol úspešne upravený.`,
          color: SUCCESS_NOTIFICATION_COLOR,
        });

        if (PREDEFINED_DATA_REDIRECT_AFTER_SAVE === 'list') {
          navigate(USERS_PREDEFINED_DATA_PAGE_PATH.original);
        }
      })
      .catch(panic);
  }

  return (
    <DashboardLayout
      title="Úprava predvoleného údaju"
      breadcrumbs={[
        { title: 'Nastavenia' },
        { title: 'Predvolené údaje', link: USERS_PREDEFINED_DATA_PAGE_PATH.original },
        { title: 'Úprava predvoleného údaju' },
      ]}
    >
      {!loading && (
        <PreDefinedDataForm
          initialValues={{
            predefinedDataName: data?.key ?? '',
            deviceTypeId: String(data?.deviceType.deviceTypeId) ?? '',
            predefinedDataDescription: data?.value ?? '',
            predefinedDataTypeId: String(data?.predefinedDataType.predefinedDataTypeId) ?? '',
            isGlobal: false,
            isPersonal: true,
          }}
          onSubmit={onSubmit}
          readOnly={{
            predefinedDataTypeId: true,
            deviceTypeId: true,
          }}
        />
      )}
    </DashboardLayout>
  );
}
